import { useState, useEffect } from 'react';
import { 
  fetchBlogCategories, 
  addBlogCategory, 
  updateBlogCategory, 
  deleteBlogCategory, 
  bulkDeleteBlogCategories 
} from '../../services/AdminBlogService';

export const useBlogCategoryActions = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
  });

  // Fetch categories
  const fetchData = async () => {
    try {
      setLoading(true);
      const categoriesData = await fetchBlogCategories();
      setCategories(categoriesData);
      setError(null);
    } catch (err) {
      setError('Error fetching data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter categories based on current filters
  const filteredCategories = categories.filter(category => {
    const matchesSearch = !filters.search || 
      category.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      category.description.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesStatus = !filters.status || 
      (filters.status === 'active' ? category.is_active : !category.is_active);

    return matchesSearch && matchesStatus;
  });

  // Add new category
  const handleAddCategory = async (categoryData) => {
    try {
      const newCategory = await addBlogCategory(categoryData);
      setCategories(prevCategories => [newCategory, ...prevCategories]);
      return newCategory;
    } catch (err) {
      setError('Error adding category');
      throw err;
    }
  };

  // Update existing category
  const handleUpdateCategory = async (id, categoryData) => {
    try {
      const updatedCategory = await updateBlogCategory(id, categoryData);
      setCategories(prevCategories => 
        prevCategories.map(category => 
          category.id === id ? updatedCategory : category
        )
      );
      return updatedCategory;
    } catch (err) {
      setError('Error updating category');
      throw err;
    }
  };

  // Delete single category
  const handleDeleteCategory = async (id) => {
    try {
      await deleteBlogCategory(id);
      setCategories(prevCategories => prevCategories.filter(category => category.id !== id));
      setSelectedCategories(prev => prev.filter(categoryId => categoryId !== id));
    } catch (err) {
      setError('Error deleting category');
      throw err;
    }
  };

  // Bulk delete categories
  const handleBulkDeleteCategories = async () => {
    if (selectedCategories.length === 0) return;
    
    try {
      await bulkDeleteBlogCategories(selectedCategories);
      setCategories(prevCategories => 
        prevCategories.filter(category => !selectedCategories.includes(category.id))
      );
      setSelectedCategories([]);
    } catch (err) {
      setError('Error bulk deleting categories');
      throw err;
    }
  };

  // Selection handlers
  const handleSelectCategory = (categoryId, checked) => {
    if (checked) {
      setSelectedCategories(prev => 
        prev.includes(categoryId) ? prev : [...prev, categoryId]
      );
    } else {
      setSelectedCategories(prev => 
        prev.filter(id => id !== categoryId)
      );
    }
  };

  const handleSelectAllCategories = (checked) => {
    if (checked) {
      setSelectedCategories(filteredCategories.map(category => category.id));
    } else {
      setSelectedCategories([]);
    }
  };

  const clearSelectedCategories = () => {
    setSelectedCategories([]);
  };

  // Filter handlers
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setSelectedCategories([]); // Clear selection when filters change
  };

  // Wrapper for SearchFilter component
  const handleFilter = (searchTerm) => {
    handleFilterChange('search', searchTerm);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
    });
    setSelectedCategories([]);
  };

  return {
    // Data
    filteredCategories: filteredCategories,
    categories: categories,
    loading,
    isLoading: loading,
    error,
    selectedCategories,
    filters,
    
    // Actions
    loadCategories: fetchData,
    handleAddCategory,
    handleUpdateCategory,
    handleDeleteCategory,
    handleBulkDeleteCategories,
    
    // Selection
    handleCategorySelect: handleSelectCategory,
    handleSelectAll: handleSelectAllCategories,
    clearSelectedCategories,
    
    // Filters
    handleFilter: handleFilter,
    clearFilters,
  };
}; 